/**
 * Daily Revenue Chart Component
 *
 * Shows daily revenue trend as a line chart
 */

import { Maximize2, RotateCcw, X } from "lucide-react";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Brush,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PriceDisplay } from "@/components/ui/price-display";
import { useIsMobile } from "@/lib/hooks/use-mobile";
import { formatLocalizedDate } from "../lib/format-localized-date";
import { MetricTooltip } from "./metric-tooltip";

interface DailyRevenueChartProps {
  data: Array<{ date: string; revenue: number }>;
}

interface TravellerProps {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Threshold above which the chart is considered "long-range": desktop
// shows a Brush, mobile shows a tap-to-view landscape modal.
const LONG_RANGE_THRESHOLD_DAYS = 60;

function formatXAxisDateFactory(language: string, totalDays: number) {
  return (dateStr: string) => {
    const dt = DateTime.fromISO(dateStr, { zone: "utc" });
    if (!dt.isValid) return dateStr;
    const tickFormat = totalDays > 180 ? "MMM yyyy" : "MMM d";
    return formatLocalizedDate(dt, tickFormat, language);
  };
}

function formatTooltipDate(dateStr: string, language: string): string {
  const dt = DateTime.fromISO(dateStr, { zone: "utc" });
  if (!dt.isValid) {
    const jsDate = new Date(dateStr);
    if (!Number.isNaN(jsDate.getTime())) {
      return formatLocalizedDate(
        DateTime.fromJSDate(jsDate, { zone: "utc" }),
        "MMM d, yyyy",
        language
      );
    }
    return dateStr;
  }
  return formatLocalizedDate(dt, "MMM d, yyyy", language);
}

function ChartBody({
  data,
  language,
  showBrush,
}: {
  data: DailyRevenueChartProps["data"];
  language: string;
  showBrush: boolean;
}) {
  const brushEndIndex = data.length - 1;
  const brushStartIndex = Math.max(0, brushEndIndex - 29);
  const labelDataLength = showBrush ? brushEndIndex - brushStartIndex + 1 : data.length;
  const xAxisInterval = Math.max(0, Math.floor((labelDataLength - 1) / 12));
  const formatXAxis = formatXAxisDateFactory(language, data.length);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis
          dataKey="date"
          className="text-xs"
          tick={{ fill: "currentColor" }}
          tickFormatter={formatXAxis}
          interval={xAxisInterval}
          angle={-45}
          textAnchor="end"
          height={80}
        />
        <YAxis
          className="text-xs"
          tick={{ fill: "currentColor" }}
          tickFormatter={(value) => `${(value / 100).toFixed(0)}`}
        />
        <Tooltip
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              const p = payload[0];
              return (
                <div className="bg-popover border rounded-md px-3 py-2 shadow-md">
                  <p className="text-xs text-muted-foreground mb-1">
                    {formatTooltipDate(p.payload.date, language)}
                  </p>
                  <p className="text-sm font-medium">
                    <PriceDisplay amount={p.value as number} />
                  </p>
                </div>
              );
            }
            return null;
          }}
        />
        <Line
          type="monotone"
          dataKey="revenue"
          stroke="#3b82f6"
          strokeWidth={2}
          dot={{ fill: "#3b82f6", r: 3 }}
          activeDot={{ r: 5 }}
          isAnimationActive={true}
          animationDuration={1000}
          animationEasing="ease-out"
        />
        {showBrush ? (
          <Brush
            dataKey="date"
            height={36}
            stroke="#94a3b8"
            fill="rgba(59, 130, 246, 0.08)"
            travellerWidth={12}
            traveller={(props: TravellerProps) => {
              const { x, y, width, height } = props;
              const cx = x + width / 2;
              const cy = y + height / 2;
              return (
                <g pointerEvents="all">
                  {/* Shadow */}
                  <rect
                    x={x}
                    y={y + 1}
                    width={width}
                    height={height}
                    rx={width / 2}
                    ry={width / 2}
                    fill="rgba(0,0,0,0.06)"
                  />
                  {/* Handle body */}
                  <rect
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    rx={width / 2}
                    ry={width / 2}
                    fill="#ffffff"
                    stroke="#cbd5e1"
                    strokeWidth={1}
                  />
                  {/* Grip lines */}
                  <line
                    x1={cx - 2.5}
                    x2={cx - 2.5}
                    y1={cy - 5}
                    y2={cy + 5}
                    stroke="#94a3b8"
                    strokeWidth={1.2}
                    strokeLinecap="round"
                  />
                  <line
                    x1={cx}
                    x2={cx}
                    y1={cy - 5}
                    y2={cy + 5}
                    stroke="#94a3b8"
                    strokeWidth={1.2}
                    strokeLinecap="round"
                  />
                  <line
                    x1={cx + 2.5}
                    x2={cx + 2.5}
                    y1={cy - 5}
                    y2={cy + 5}
                    stroke="#94a3b8"
                    strokeWidth={1.2}
                    strokeLinecap="round"
                  />
                </g>
              );
            }}
            startIndex={brushStartIndex}
            endIndex={brushEndIndex}
            tickFormatter={formatXAxis}
            className="brush-rounded [&_text]:fill-slate-400 [&_text]:text-[11px] [&_.recharts-brush-texts]:translate-y-1"
          />
        ) : null}
      </LineChart>
    </ResponsiveContainer>
  );
}

export function DailyRevenueChart({ data }: DailyRevenueChartProps) {
  const { t, i18n } = useTranslation("reports");
  const hasData = data && data.length > 0;
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  // Long-range = a chart wide enough that a tiny mobile portrait viewport
  // would render an unreadable line. We swap in a tap-to-view landscape
  // modal for those cases instead of cramming a Brush onto a 360px-wide
  // screen.
  const isLongRange = hasData && data.length > LONG_RANGE_THRESHOLD_DAYS;
  const showBrush = isLongRange && !isMobile;

  // Lock body scroll while the fullscreen view is open so the chart owns
  // the whole viewport instead of jiggling around with the page.
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const title = t("charts.dailyRevenue.title");
  const tooltipContent = t("charts.dailyRevenue.tooltip");

  if (!hasData) {
    return (
      <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500 h-full">
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center">
            {title}
            <MetricTooltip content={tooltipContent} />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[300px]">
            <p className="text-sm text-muted-foreground">No data</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Mobile long-range: blurred preview + tap-to-view CTA + fullscreen
  // landscape modal. The rotation gives the chart the long axis to use
  // without asking the user to physically turn the device.
  if (isMobile && isLongRange) {
    return (
      <>
        <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500 h-full">
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center">
              {title}
              <MetricTooltip content={tooltipContent} />
            </CardTitle>
          </CardHeader>
          <CardContent className="relative h-[300px] overflow-hidden rounded-b-xl p-0">
            <div className="pointer-events-none select-none blur-[3px] opacity-60 h-full">
              <ChartBody data={data} language={i18n.language} showBrush={false} />
            </div>
            <div className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-[1px]">
              <Button
                type="button"
                onClick={() => setIsOpen(true)}
                size="lg"
                className="shadow-lg flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
              >
                <Maximize2 className="h-4 w-4" />
                {t("charts.dailyRevenue.tapToView")}
              </Button>
            </div>
          </CardContent>
        </Card>

        {isOpen ? (
          <FullscreenLandscapeChart
            data={data}
            language={i18n.language}
            title={title}
            hint={t("charts.dailyRevenue.landscapeHint")}
            ariaClose={t("charts.dailyRevenue.closeChart")}
            onClose={() => setIsOpen(false)}
          />
        ) : null}
      </>
    );
  }

  return (
    <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500 h-full">
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center">
          {title}
          <MetricTooltip content={tooltipContent} />
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div style={{ height: showBrush ? 340 : 300 }}>
          <ChartBody data={data} language={i18n.language} showBrush={showBrush} />
        </div>
      </CardContent>
    </Card>
  );
}

function FullscreenLandscapeChart({
  data,
  language,
  title,
  hint,
  ariaClose,
  onClose,
}: {
  data: DailyRevenueChartProps["data"];
  language: string;
  title: string;
  hint: string;
  ariaClose: string;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/95 backdrop-blur-sm">
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 z-10 rounded-full bg-white/95 p-2 shadow-lg hover:bg-white"
        aria-label={ariaClose}
      >
        <X className="h-5 w-5 text-slate-900" />
      </button>

      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: "100vh",
          height: "100vw",
          transform: "translate(-50%, -50%) rotate(90deg)",
          transformOrigin: "center",
        }}
      >
        <div className="h-full w-full bg-white px-6 py-4 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-slate-900">{title}</h2>
            <span className="text-xs font-medium text-slate-500 flex items-center gap-1">
              <RotateCcw className="h-3 w-3" />
              {hint}
            </span>
          </div>
          <div className="flex-1 min-h-0">
            <ChartBody data={data} language={language} showBrush={true} />
          </div>
        </div>
      </div>
    </div>
  );
}
